import { CookieResponse } from '@/components/cookie-dialog';
import {
    EnumDialogName,
    EnumFeature,
    EnumScreenName,
    EnumStepName,
    EnumTheme,
    EventButtonClick,
    EventDialogView,
    EventProperties,
    EventStepView,
    SuperProperties,
    UserProperties,
    EnumEventName,
    PropBaseEventProps,
    PropBaseEventPropsV2,
} from '@calm/evexia-analytics';
import { useCookieResponse } from '@rhg/hooks/use-cookie-response';
import usePrefersDarkMode from '@rhg/hooks/use-prefers-dark-mode';
import useAuth from '@rhg/libs/auth0/use-auth';
import { getUserOs } from '@rhg/utils/browser';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { initDatadogLogs } from '../logger';
import { AnalyticsDestination } from './destination/types';
import mapBaseEventProps from './map-base-event-props';
import trackPageNav, {
    trackAsPageNav as ogTrackAsPageNav,
    getFeatureName,
    getScreenName,
} from './track-page-nav';
import {
    LoginEventProps,
    ProgramDismissProps,
    SessionEventProps,
} from './types';

interface AnalyticsDialogState {
    onDialogOpen: (dialogName: EnumDialogName) => void;
    onDialogClose: (dialogName: EnumDialogName) => void;
    currentDialog?: EnumDialogName;
}
interface AnalyticsWizardState {
    step?: EnumStepName;
    setStep: (step?: EnumStepName) => void;
}
interface AnalyticsState {
    dialog: AnalyticsDialogState;
    wizard: AnalyticsWizardState;
    currentFeature: EnumFeature;
    currentScreenName: EnumScreenName;
}

interface AnalyticsContextProps {
    destinations: AnalyticsDestination[];
    state: AnalyticsState;
    setCurrentFeature: (feature: EnumFeature) => void;
    setCurrentScreenName: (screenName: EnumScreenName) => void;
}

const AnalyticsContext = createContext<AnalyticsContextProps>(undefined!);

interface AnalyticsProviderProps {
    children: ReactNode;
    destinations: AnalyticsDestination[];
}
export const AnalyticsProvider = ({
    children,
    destinations,
}: AnalyticsProviderProps) => {
    const areDestinationsInitialized = useRef(false);
    const {
        isResponseRequired,
        isFetched: isResponseFetched,
        response,
        deviceCountry,
    } = useCookieResponse();
    const router = useRouter();
    const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
    const prefersDarkMode = usePrefersDarkMode();
    const themeName = prefersDarkMode ? EnumTheme.Dark : EnumTheme.Light;

    const [currentStep, setCurrentStep] = useState<EnumStepName | undefined>(
        undefined
    );
    const initialScreenName = getScreenName(router, isAuthenticated);
    const [currentScreenName, setCurrentScreenName] =
        useState<EnumScreenName>(initialScreenName);
    const [currentFeature, setCurrentFeature] = useState<EnumFeature>(
        getFeatureName(router)
    );
    const [openDialogs, setOpenDialogs] = useState<EnumDialogName[]>([]);

    const currentDialog = useMemo(
        () =>
            openDialogs.length > 0
                ? openDialogs[openDialogs.length - 1]
                : undefined,
        [openDialogs]
    );

    const state = useMemo(
        () =>
            ({
                dialog: {
                    currentDialog,
                    onDialogOpen: (dialogName: EnumDialogName) =>
                        setOpenDialogs([...openDialogs, dialogName]),
                    onDialogClose: (dialogName: EnumDialogName) =>
                        setOpenDialogs(
                            openDialogs.filter(
                                (openName) => openName !== dialogName
                            )
                        ),
                },
                wizard: {
                    step: currentStep,
                    setStep: (step?: EnumStepName) => setCurrentStep(step),
                },
                currentFeature,
                currentScreenName,
            } as AnalyticsState),
        // TODO: fix this eslint-disable
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            currentDialog,
            currentFeature,
            currentScreenName,
            currentStep,
            isAuthenticated,
            openDialogs,
            router.pathname,
        ]
    );

    useEffect(() => {
        initDatadogLogs();
    }, []);

    // Analytics should not be able to track until they are initialized. For Amplitude, the events are cached until we initialize
    useEffect(() => {
        if (
            (isResponseFetched && !isResponseRequired) ||
            response === CookieResponse.All
        ) {
            if (!areDestinationsInitialized.current) {
                areDestinationsInitialized.current = true;
                destinations.forEach((dest) => {
                    dest.init();
                });
                destinations.forEach((dest) => {
                    dest.setUserProperties({
                        UserDeviceCountry: deviceCountry?.toUpperCase(),
                    });
                });
            }
        }
    }, [
        destinations,
        deviceCountry,
        isResponseFetched,
        isResponseRequired,
        response,
    ]);

    const screenName = getScreenName(router, isAuthenticated);
    const featureName = getFeatureName(router);
    useEffect(() => {
        if (!isAuthLoading && router?.pathname && router.isReady) {
            setCurrentFeature(featureName);
            setCurrentScreenName(screenName);
            trackPageNav({
                pathname: router.pathname,
                query: router.query,
                themeName,
                destinations,
                isAuthenticated,
            });
        }
        // Intentionally exclude router.asPath, as we only want to track page nav
        // when the router change actually affects the screen name or feature name.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        destinations,
        isAuthLoading,
        isAuthenticated,
        router.isReady,
        screenName,
        featureName,
        themeName,
    ]);

    return (
        <AnalyticsContext.Provider
            value={{
                destinations,
                state,
                setCurrentFeature,
                setCurrentScreenName,
            }}
        >
            {children}
        </AnalyticsContext.Provider>
    );
};

type TrackExtendedTypes =
    | PropBaseEventProps
    | PropBaseEventPropsV2
    | EventProperties
    | EventButtonClick
    | EventDialogView
    | EventStepView
    | LoginEventProps
    | SessionEventProps
    | ProgramDismissProps;

export type TrackFn = <T extends TrackExtendedTypes>(
    event: EnumEventName | string,
    properties?: T
) => Promise<void>;

export const useTracking = () => {
    const { destinations, state, setCurrentFeature, setCurrentScreenName } =
        useContext(AnalyticsContext);
    const prefersDarkMode = usePrefersDarkMode();
    const { response } = useCookieResponse();

    const trackAsPageNav = useCallback(
        (
            screenName: EnumScreenName,
            feature: EnumFeature,
            query?: ParsedUrlQuery,
            additionalProps?: EventProperties
        ) => {
            if (response !== CookieResponse.Essential) {
                setCurrentFeature(feature);
                setCurrentScreenName(screenName);
                ogTrackAsPageNav({
                    screenName,
                    feature,
                    query,
                    themeName: prefersDarkMode
                        ? EnumTheme.Dark
                        : EnumTheme.Light,
                    destinations,
                    additionalProps,
                });
            }
        },
        [
            destinations,
            prefersDarkMode,
            setCurrentFeature,
            setCurrentScreenName,
            response,
        ]
    );

    const track = useCallback<TrackFn>(
        async (event, properties) => {
            if (response !== CookieResponse.Essential) {
                try {
                    await Promise.all(
                        destinations.map((dest) =>
                            dest
                                .track(event, mapBaseEventProps(properties))
                                .then(() => {
                                    if (event === EnumEventName.Logout) {
                                        dest.reset();
                                    }
                                })
                        )
                    );
                } catch {
                    return;
                }
            }
        },
        [destinations, response]
    );

    const flush = useCallback(async () => {
        if (response !== CookieResponse.Essential) {
            try {
                await Promise.all(destinations.map((dest) => dest.flush()));
            } catch {
                return;
            }
        }
    }, [destinations, response]);

    return {
        trackAsPageNav,
        track,
        flush,
        identify: async (uuid: string) => {
            if (uuid && response !== CookieResponse.Essential) {
                await Promise.all(
                    destinations.map((dest) => dest.identify(uuid))
                );
            }
        },
        /**
         * Registers
         */
        setUserProfile: async (props: UserProperties) => {
            if (props && response !== CookieResponse.Essential) {
                await Promise.all(
                    destinations.map((dest) => dest.setUserProperties(props))
                );
            }
        },
        /**
         * Registers a super property that will be sent with each event. (stored in the cookie)
         */
        register: async (props: SuperProperties) => {
            if (props && response !== CookieResponse.Essential) {
                await Promise.all(
                    destinations.map((dest) => dest.register(props))
                );
            }
        },
        state,
    };
};

export function TrackDeviceType({ children }: { children: ReactNode }) {
    const { setUserProfile } = useTracking();
    const isTracked = useRef(false);
    const os = getUserOs();
    useEffect(() => {
        if (!isTracked.current) {
            setUserProfile({
                'Device type': os.name,
            });
            isTracked.current = true;
        }
    }, [setUserProfile, os]);
    return <>{children}</>;
}
